import React, { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { FieldRenderProps } from 'react-final-form';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import UICancelButton from 'Components/UIComponents/UICancelButton';
import UIProgressBar from 'Components/UIComponents/UIProgressBar';
import useIsMobile from 'Hooks/Common/useIsMobile';
import { DocumentItem, FileItem } from 'Interfaces/Common';
import { uniq, values } from 'lodash-es';
import Toast from 'Services/Toast';
import { MIME_TYPES } from 'Utils/constants';
import { moveArrayItem } from 'Utils/functions';
import {
  lessThan40MB,
  maxLength100,
  multipleFilesConstaint,
  singleFileConstaint,
} from 'Utils/validation';

import { UploaderFormOptions, UploaderPageTypes } from '../common/constant';

import { LandingUploaderActions } from './LandingUploaderActions';

import FileSVG from 'Assets/images/icons/documents-icon.svg';

const defaultFormats = uniq(values(MIME_TYPES)).join(',');

interface UploaderClassNames {
  uploadZoneClassName?: string;
  buttonClassName?: string;
  buttonPriority: 'primary' | 'secondary';
}

interface LandingUploaderProps extends FieldRenderProps<string>, UploaderFormOptions {
  disabled?: boolean;
  files?: DocumentItem[];
  onFileUpload: (file: File) => Promise<void>;
  onUploadCancel?: (fileItem: FileItem) => Promise<void>;
  onUploadSuccess?: () => void;
  onUploadFailure?: (error: string) => void;
  isFileProcessed: boolean;
  submitting: boolean;
  onFileReorder: (orders: FileItem[]) => void;
  disableReorder?: boolean;
  isCleanFileData?: boolean;
  isShowWarning?: boolean;
  disableImportServices?: boolean;
  filesLimit?: number;
  acceptableFormats?: string;
  reopenInteract?: () => void;
}

function LandingUploader({
  disabled,
  onUploadCancel,
  onFileUpload,
  files = [],
  isFileProcessed,
  submitting,
  onUploadSuccess = () => {},
  onUploadFailure = () => {},
  onFileReorder,
  disableReorder,
  isCleanFileData,
  isShowWarning,
  filesLimit,
  acceptableFormats = defaultFormats,
  buttonText,
  headerTitle,
  uploadTitle,
  pageType = UploaderPageTypes.DEFAULT,
  reopenInteract,
}: LandingUploaderProps) {
  const isMobile = useIsMobile();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const isError = useMemo(
    () => !!uploadError || (files && files.some(file => !!file.errorText)),
    [files, uploadError],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        await onFileUpload(file);

        setUploadError(null);
        onUploadSuccess();
      } catch (error) {
        setUploadError('Failed to upload');
        const e = error as Error;
        onUploadFailure(e.message);
      }
    },
    [onFileUpload, onUploadFailure, onUploadSuccess],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const documentFiles = files.map(item => item.file);
      const error =
        filesLimit === 1
          ? singleFileConstaint([...documentFiles, ...acceptedFiles])
          : multipleFilesConstaint([...documentFiles, ...acceptedFiles], filesLimit);

      if (error) {
        return Toast.error(error);
      }

      acceptedFiles.forEach(file => {
        const fileMeta = file;

        if (disabled || !fileMeta) return;

        const error = lessThan40MB(fileMeta.size) || maxLength100(fileMeta.name);

        if (error) {
          return Toast.error(error);
        }
        handleUpload && handleUpload(fileMeta);
      });
    },
    [disabled, files, filesLimit, handleUpload],
  );

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    onDrop,
    accept: acceptableFormats,
  });

  const handleDragEnd = useCallback(
    ({ source, destination }) => {
      if (destination) {
        const newOrders = moveArrayItem(files, source.index, destination.index).map(
          (value, index) => {
            return { ...value, order: index + 1 };
          },
        );

        onFileReorder(newOrders);
      }
    },
    [files, onFileReorder],
  );

  const uploaderClassNames: UploaderClassNames = useMemo(() => {
    if (pageType === UploaderPageTypes.BLOG) {
      return {
        uploadZoneClassName: undefined,
        buttonClassName: classNames('sign-up-landing__upload-button', {
          [pageType]: pageType,
          'upload--disabled': disabled,
        }),
        buttonPriority: 'primary',
      };
    }

    return {
      uploadZoneClassName: classNames('upload__dropzone', {
        'upload__dropzone--error': isError || isDragReject,
        'upload__dropzone--success': isFileProcessed && !isError,
        'upload--disabled': disabled,
      }),
      buttonClassName: classNames({ 'upload--disabled': disabled }),
      buttonPriority: 'secondary',
    };
  }, [disabled, isDragReject, isError, isFileProcessed, pageType]);

  return (
    <div className="signTemplate__templateField-select-wrapper">
      <div
        className={classNames(
          'signTemplate__templateField-upload-wrapper',
          'sign-up-landing__upload-content--wrapper',
        )}
      >
        <div className="upload">
          <div className={classNames('upload__body', { mobile: isMobile })}>
            <div className={classNames('upload__block', 'upload__block--fill')}>
              <div
                className={classNames(
                  'upload__title upload__title-header',
                  'sign-up-landing__upload-subtitle',
                  { [pageType]: pageType },
                )}
              >
                {headerTitle}
              </div>
              <div {...getRootProps()} className={uploaderClassNames.uploadZoneClassName}>
                <input {...getInputProps()} disabled={disabled} />
                <LandingUploaderActions
                  buttonText={buttonText}
                  buttonClassName={uploaderClassNames.buttonClassName}
                  buttonPriority={uploaderClassNames.buttonPriority}
                  disabled={disabled}
                  isError={isError}
                  isFileProcessed={isFileProcessed}
                  pageType={pageType}
                  submitting={submitting}
                  uploadTitle={uploadTitle}
                  reopenInteract={reopenInteract}
                />
              </div>
              {isError && <div className="upload__error">{uploadError}</div>}
            </div>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="fileItems">
              {droppableProvided => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {files.map((fileItem, index) => (
                    <Draggable
                      draggableId={`fileItem_${fileItem.token}`}
                      index={index}
                      key={fileItem.token}
                      isDragDisabled={disableReorder}
                    >
                      {draggableProvided => (
                        <div
                          className={classNames('upload__status', {
                            'sign-up-landing__upload-progress--wrapper':
                              pageType === UploaderPageTypes.BLOG,
                          })}
                          key={fileItem.token}
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          {pageType !== UploaderPageTypes.BLOG && (
                            <div className="upload__status-header">
                              <div className="upload__status-item upload__status-file">
                                <ReactSVG src={FileSVG} />
                              </div>
                              <div className="upload__status-text upload__status-item">
                                {fileItem.filename}
                              </div>
                              {onUploadCancel && (
                                <UICancelButton
                                  disabled={isCleanFileData}
                                  onClick={() => onUploadCancel(fileItem)}
                                  isLoading={isCleanFileData}
                                />
                              )}
                            </div>
                          )}
                          {!!fileItem.progress &&
                            !fileItem.isFinished &&
                            !fileItem.errorText && (
                              <div
                                className={classNames('upload__progress-bar', {
                                  'sign-up-landing__upload-progress--container':
                                    pageType === UploaderPageTypes.BLOG,
                                })}
                              >
                                <UIProgressBar
                                  percent={fileItem.progress}
                                  containerClassName={
                                    pageType === UploaderPageTypes.BLOG
                                      ? 'sign-up-landing__upload-progress--bar-container'
                                      : undefined
                                  }
                                  runningLineClassName={
                                    pageType === UploaderPageTypes.BLOG
                                      ? 'sign-up-landing__upload-progress--filter'
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                          {fileItem.errorText && (
                            <div className="upload__error-text">{fileItem.errorText}</div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        {isShowWarning && (
          <div className="signTemplate__upload-notice">
            <p className="signTemplate__upload-notice symbol">*</p>
            <p className="signTemplate__upload-notice text">
              To keep your email box tidy, Signaturely only attaches documents under 10Mb
              as PDF files to the email sent when they get signed. You will still be able
              to download larger files from the Download link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LandingUploader;
